import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getPortfolioSummary, getLatestPortfolioBalance, getPortfolioAllocation } from '../../api/metrics';
import { getActiveTradeGroupsDetails } from '../../api/tradeGroups';
import { WalletIcon, TargetIcon, ShoppingCartIcon, LayersIcon, RefreshIcon, BarChartIcon, ClockIcon, TrendingUpIcon } from '../shared/Icons';
import StandaloneSection from '../shared/StandaloneSection';
import '../shared/MetricCards.css';
import './PortfolioSummary.css';

/**
 * Format currency with $ sign and 2 decimal places
 */
const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Format percentage
 */
const formatPercentage = (value: number): string => {
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
};

/**
 * Metric Card Component
 */
interface MetricCardProps {
  title: string;
  value: string;
  change?: string;
  icon: React.ReactNode;
  variant?: 'default' | 'success' | 'danger' | 'warning' | 'info';
  size?: 'small' | 'medium' | 'large';
  subtitle?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  change,
  icon,
  variant = 'default',
  size = 'medium',
  subtitle
}) => {
  return (
    <div className={`metric-card metric-card-${variant} metric-card-${size}`}>
      <div className="metric-icon">{icon}</div>
      <div className="metric-content">
        <div className="metric-title">{title}</div>
        <div className="metric-value">{value}</div>
        {change && <div className="metric-change">{change}</div>}
        {subtitle && <div className="metric-subtitle">{subtitle}</div>}
      </div>
    </div>
  );
};

/**
 * Portfolio Summary Card Component
 * Displays key portfolio metrics including P&L, cost basis, and open orders
 */
interface PortfolioSummaryProps {
  onSnapshotTimeUpdate?: (timestamp: number) => void;
}

const PortfolioSummary: React.FC<PortfolioSummaryProps> = ({ onSnapshotTimeUpdate }) => {
  // Fetch latest portfolio balance from Balance API (for hero metrics)
  const { data: balanceData, isLoading: balanceLoading, isError: balanceError, error: balanceErrorObj } = useQuery({
    queryKey: ['portfolio', 'balance', 'latest'],
    queryFn: getLatestPortfolioBalance,
    retry: 2,
  });

  // Fetch portfolio allocation from Balance API (for unrealized P&L)
  const { data: allocationData, isLoading: allocationLoading, isError: allocationError } = useQuery({
    queryKey: ['portfolio', 'allocation'],
    queryFn: getPortfolioAllocation,
    retry: 2,
  });

  // Fetch portfolio summary from Trade Stats (for metric cards)
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['portfolio-summary'],
    queryFn: getPortfolioSummary,
    retry: 2,
  });

  // Fetch detailed trade group data for enhanced metrics
  const { data: tradeGroupsData } = useQuery({
    queryKey: ['active-trade-groups-details'],
    queryFn: getActiveTradeGroupsDetails,
    retry: 2,
  });

  // Notify parent component of snapshot time whenever balance data updates
  useEffect(() => {
    if (balanceData?.snapshotTime && onSnapshotTimeUpdate) {
      onSnapshotTimeUpdate(balanceData.snapshotTime);
    }
  }, [balanceData?.snapshotTime, onSnapshotTimeUpdate]);

  if (isLoading || balanceLoading || allocationLoading) {
    return (
      <StandaloneSection title="PORTFOLIO SUMMARY">
        <div className="chart-loading">
          <div className="loading-spinner"></div>
          <p>Loading portfolio data...</p>
        </div>
      </StandaloneSection>
    );
  }

  if (isError || balanceError || allocationError) {
    return (
      <StandaloneSection title="PORTFOLIO SUMMARY">
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <p>Unable to load portfolio data</p>
          <p className="error-detail">
            {balanceError
              ? (balanceErrorObj instanceof Error ? balanceErrorObj.message : 'Failed to load balance data')
              : (error instanceof Error ? error.message : 'Please try again later')
            }
          </p>
        </div>
      </StandaloneSection>
    );
  }

  if (!data || !balanceData || !allocationData) return null;

  // Parse Balance API data (BigDecimal strings to numbers)
  const totalBalance = parseFloat(balanceData.totalBalanceValue);
  const cashBalance = parseFloat(balanceData.totalCashEquivalentBalanceValue);
  const cryptoBalance = parseFloat(balanceData.totalCryptoBalanceValue);

  // Calculate percentages for cash and crypto
  const cashPercent = totalBalance > 0 ? (cashBalance / totalBalance) * 100 : 0;
  const cryptoPercent = totalBalance > 0 ? (cryptoBalance / totalBalance) * 100 : 0;

  // Calculate total profit for 24h change placeholder
  const totalProfit = (data.totalRealizedProfit || 0) + (data.totalUnrealizedProfit || 0);

  // Calculate keep-alive stats from trade groups data
  const keepAliveStats = tradeGroupsData?.tradeGroups
    ? tradeGroupsData.tradeGroups.reduce((acc, group) => ({
        totalKeepAlive: acc.totalKeepAlive + group.keepAliveCount,
        groupsWithKeepAlive: acc.groupsWithKeepAlive + (group.keepAliveCount > 0 ? 1 : 0)
      }), { totalKeepAlive: 0, groupsWithKeepAlive: 0 })
    : { totalKeepAlive: 0, groupsWithKeepAlive: 0 };

  // TODO: Calculate 24-hour change when we have historical balance data
  // For now, showing positive indicator based on realized profit
  const change24h = totalProfit; // Placeholder - will be replaced with actual 24h delta
  const changePercent24h = totalBalance > 0 ? (change24h / totalBalance) * 100 : 0;

  return (
    <StandaloneSection title="PORTFOLIO SUMMARY">
      {/* Primary Metrics - Hero Section (from Balance API) */}
      <div className="hero-metrics">
        <div className="hero-metric hero-metric-primary">
          <div className="hero-label">Total Balance</div>
          <div className={`hero-value-primary`}>
            {formatCurrency(totalBalance)}
          </div>
          <div className={`hero-change ${change24h >= 0 ? 'positive' : 'negative'}`}>
            {formatCurrency(change24h)} ({formatPercentage(changePercent24h)})
          </div>
        </div>

          <div className="hero-metric">
              <div className="hero-label">Crypto Balance</div>
              <div className={`hero-value`}>
                  {formatCurrency(cryptoBalance)}
              </div>
              <div className="hero-change-neutral">
                  {cryptoPercent.toFixed(1)}% of portfolio
              </div>
          </div>

        <div className="hero-metric">
          <div className="hero-label">Cash Balance</div>
          <div className={`hero-value`}>
            {formatCurrency(cashBalance)}
          </div>
          <div className="hero-change-neutral">
            {cashPercent.toFixed(1)}% of portfolio
          </div>
        </div>


      </div>

      {/* Secondary Metrics Grid - Hybrid: Balance API + Trade Stats */}
      <div className="metrics-grid">

        <MetricCard
          title="Overall P&L"
          value={formatCurrency(data.totalCombinedPnl)}
          subtitle="Realized + Unrealized"
          icon={<TrendingUpIcon size={24} className="icon-text"/>}
          variant={data.totalCombinedPnl >= 0 ? 'success' : 'danger'}
        />

        <MetricCard
          title="Realized P&L"
          value={formatCurrency(data.totalRealizedProfit)}
          subtitle="Completed trades"
          icon={<TrendingUpIcon size={24} className="icon-text"/>}
          variant={data.totalRealizedProfit >= 0 ? 'success' : 'danger'}
        />

        <MetricCard
          title="Unrealized P&L"
          value={formatCurrency(data.totalUnrealizedProfit)}
          subtitle="Current market value"
          icon={<TrendingUpIcon size={24} className="icon-text"/>}
          variant={data.totalUnrealizedProfit >= 0 ? 'success' : 'danger'}
        />

        <MetricCard
          title="Target P&L"
          value={formatCurrency(data.totalTargetProfit)}
          subtitle="On open positions"
          icon={<TargetIcon size={24} className="icon-text"/>}
          variant={data.totalTargetProfit >= 0 ? 'success' : 'danger'}
        />

          <MetricCard
              title="Cost Basis"
              value={formatCurrency(data.totalCostBasis)}
              subtitle="Total deployed capital"
               icon={<BarChartIcon size={24} className="icon-text" />}
              variant="default"
          />

          <MetricCard
              title="Pending Buys"
              value={formatCurrency(data.pendingBuyOrdersValue)}
              subtitle="Capital committed"
              icon={<ShoppingCartIcon size={24} className="icon-text" />}
              variant="default"
          />

        <MetricCard
          title="Allocated Cash"
          value={formatCurrency(parseFloat(allocationData.summary.reservedCash))}
          subtitle="Allocated to orders"
          icon={<ClockIcon size={24} className="icon-text" />}
          variant="warning"
        />

          <MetricCard
              title="Available Cash"
              value={formatCurrency(parseFloat(allocationData.summary.availableCash))}
              subtitle="Ready for trading"
              icon={<WalletIcon size={24} className="icon-text" />}
              variant="success"
          />

        <MetricCard
          title="Keep-Alive Orders"
          value={keepAliveStats.totalKeepAlive.toString()}
          subtitle={`${keepAliveStats.groupsWithKeepAlive} groups reinvesting`}
          icon={<RefreshIcon size={24} className="icon-text" />}
          variant={keepAliveStats.totalKeepAlive > 0 ? 'success' : 'default'}
        />

        <MetricCard
          title="Active Groups"
          value={data.activeTradeGroups.toString()}
          subtitle="Trading strategies"
          icon={<LayersIcon size={24} className="icon-text" />}
          variant="success"
        />
      </div>
    </StandaloneSection>
  );
};

export default PortfolioSummary;
